import * as React from 'react';
import { styled } from '@glitz/react';
import { translate, useDynamicData, useMatchMobile } from '@avensia/excite';
import { changeLineItemQuantity, removeLineItem } from '../commands';
import { ImagePlaceholder, ImageWithBackgroundColor, SquareImage } from 'webapp/components/image';
import { Button } from 'webapp/components/button';
import SvenssonsPrice from '../../../../sites/svenssons/src/app/product/Price';
import GeneralPrice from 'nordicnest/product/Price';
import { StockStatus as GeneralStockStatus } from 'nordicnest/product/StockStatus';
import { StockStatus as SvenssonsStockStatus } from '../../../../sites/svenssons/src/app/product/StockStatus';
import { createMinLargeViewportDecorator } from 'webapp/viewport';
import Link from 'webapp/components/Link';
import { absoluteCenterDecorator } from 'webapp/decorators/center';
import { overflowEllipsisDecorator } from 'webapp/decorators/text';
import { removeQuoteFromCart } from 'webapp/pages/checkout/commands';
import { SlideHeightAnimation } from '@avensia/excite-ui';
import { useCurrentPageIsCheckoutPage } from 'webapp/pages/checkout/use-current-page-is-checkout-page';
import { Variant } from 'webapp/product/price-variant';
import { CloseIcon } from 'webapp/components/icons';
import { NumberField } from 'webapp/components/form/number-field';
import { isNordicNest, isSvenssons, Theme } from 'webapp/theme';
import { FieldSize } from 'webapp/components/field-size';
import { bareListDecorator } from 'webapp/decorators/list';
import { TestIdProps } from '@avensia/test-automation';
import { SiteLogo } from 'webapp/shared/logos';
import { useShowCartEvent } from 'webapp/tracking/events/checkout';

export function isQuoteLineItem(
  lineItem: Excite.Commercetools.LineItemApiModel,
): lineItem is Excite.Quotes.QuoteLineItemApiModel {
  return Boolean((lineItem as Excite.Quotes.QuoteLineItemApiModel).includedInQuote);
}

type PropType = {
  compact?: boolean;
  disabledEdit?: boolean;
};

export const Cart = styled(({ compact, disabledEdit }: PropType) => {
  const isCheckoutPage = useCurrentPageIsCheckoutPage();
  const [items] = useDynamicData<
    Excite.Commercetools.CartApiModel,
    Excite.Commercetools.PackageLineItemApiModel[] | undefined
  >({
    selector: cartItemsSelector,
  });

  const [plannedDeliveries] = useDynamicData<
    Excite.Commercetools.CartApiModel,
    Excite.Commercetools.CartPlannedDelivery[] | undefined
  >({
    selector: x => x?.viewData?.plannedDeliveries,
  });

  useShowCartEvent(true);

  const [unavailableLineItemIds] = useDynamicData<Excite.Commercetools.CheckoutApiModel, string[] | undefined>({
    selector(data) {
      if (data) {
        return data.checkoutStatus && Object.keys(data.checkoutStatus.unavailableLineItemIds);
      }
    },
  });

  const deliveries = React.useMemo(() => {
    return plannedDeliveries
      ?.map(delivery => {
        const deliveryItems = items?.filter(item => delivery.lineItems.includes(item.id));
        return { ...delivery, items: deliveryItems };
      })
      .filter(delivery => delivery.items?.length)
      .sort((a, b) => a.shippingOrder - b.shippingOrder);
  }, [plannedDeliveries, items]);

  if (items && deliveries) {
    return (
      <>
        {deliveries?.map((delivery, index) => (
          <React.Fragment key={index}>
            <DeliveryHeightAnimation visible={isCheckoutPage && deliveries.length > 1}>
              <DeliveryTitle>{`${translate(t => t.cart.deliveryTitle)} ${delivery.shippingOrder}`}</DeliveryTitle>
            </DeliveryHeightAnimation>
            <Items>
              {delivery?.items?.map(item => {
                return (
                  <Item
                    key={item.url}
                    item={item}
                    compact={compact}
                    unavailableLineItemIds={unavailableLineItemIds}
                    disabledEdit={disabledEdit}
                  />
                );
              })}
            </Items>
          </React.Fragment>
        ))}
      </>
    );
  }

  return null;
});

function Item({
  item,
  compact,
  unavailableLineItemIds,
  disabledEdit,
}: {
  item: Excite.Commercetools.PackageLineItemApiModel;
  compact: boolean | undefined;
  unavailableLineItemIds: string[] | undefined;
  disabledEdit: boolean | undefined;
}) {
  const [validationMessage, setValidationMessage] = React.useState<string>();
  const addImageDecorator =
    item.includedInPackage || Object.entries(item.viewData.selectedConfigurationLabel ?? {}).length > 0;
  const isMobile = useMatchMobile();
  compact ??= isMobile;

  return (
    <ListItem>
      {isQuoteLineItem(item) ? (
        <ImageWrapper>
          <LogoImagePlaceholder>
            <Logo />
          </LogoImagePlaceholder>
        </ImageWrapper>
      ) : (
        <ImageLink
          css={addImageDecorator && imageHeightDecorator}
          title={item.name}
          to={item.url}
          {...TestIdProps.MiniCartItemElement}
        >
          <Image
            image={item.image}
            external={Boolean(item.viewData.selectedConfiguration)}
            preset={'thumb'}
            imageCss={imageDecortator}
          />
        </ImageLink>
      )}
      <Brand>
        {item.viewData.brandName}
        {!disabledEdit && (
          <Remove
            {...TestIdProps.RemoveMiniCartItemButton}
            aria-label={translate(t => t.miniCart.removeItem)}
            as={Theme.Transparent}
            onClick={() =>
              isQuoteLineItem(item)
                ? removeQuoteFromCart(item.id)
                : removeLineItem(item.id, {
                    eventData: {
                      sku: item.sku,
                      additionalProductAttributes: {
                        ecommerce: { button_location: 'cart', button_name: 'remove_from_cart' },
                      },
                    },
                  })
            }
          >
            <CloseIcon />
          </Remove>
        )}
      </Brand>

      <Title>
        {isQuoteLineItem(item) ? (
          item.name
        ) : (
          <Link title={item.name} to={item.url}>
            {item.name}, {item.viewData.variant}
          </Link>
        )}
      </Title>

      {(isQuoteLineItem(item) || item.includedInPackage || item.viewData.selectedConfigurationLabel) && (
        <PackageItems>
          {item.includedInPackage && (
            <>
              <PackageIncludeText>{translate(t => t.package.packageIncludes)}</PackageIncludeText>
              <List>
                {item.includedInPackage.map(i =>
                  i.url ? (
                    <li>
                      <PackageItem to={i.url} key={i.sku}>
                        {i.viewData.packagePartQuantity} x {i.name} {i.viewData.variant}
                      </PackageItem>
                    </li>
                  ) : (
                    <li>
                      <NoLinkPackageItem key={i.sku}>
                        {i.viewData.packagePartQuantity} x {i.name} {i.viewData.variant}
                      </NoLinkPackageItem>
                    </li>
                  ),
                )}
              </List>
            </>
          )}
          {isQuoteLineItem(item) &&
            item.includedInQuote.map(i => (
              <QuoteItem to={i.url} key={i.sku}>
                {i.quantity} x {i.name} {i.viewData.variant}
              </QuoteItem>
            ))}
          {Object.entries(item.viewData.selectedConfigurationLabel ?? {}).map(([key, value]) => (
            <ProductConfigItem key={key}>
              {key} : {value}
            </ProductConfigItem>
          ))}
        </PackageItems>
      )}

      {!isQuoteLineItem(item) && (
        <ProductStockStatus
          stockStatus={unavailableLineItemIds?.includes(item.id) ? 'outOfStock' : item.viewData.stockStatus}
          variantStatus={item.viewData.productStatus}
          deliveryTime={item.viewData.deliveryTime}
        />
      )}
      <Bottom css={!compact && bottomGridDecorator}>
        {!isQuoteLineItem(item) && !disabledEdit ? (
          <>
            <Quantity
              disabled={item.viewData.isGiftItem}
              as={isSvenssons ? Theme.Transparent : Theme.Dark}
              value={item.quantity}
              min={0}
              max={10000}
              size={FieldSize.XSmall}
              onDebouncedUpdate={quantity => changeQuantity(quantity, item.id, item.sku, setValidationMessage)}
            />
          </>
        ) : (
          <span />
        )}
        {!compact && !isQuoteLineItem(item) && (
          <PriceWithLabel>
            <Label>{translate(t => t.miniCart.pricePerUnitLabel)}</Label>
            <UnitPrice price={item.unitPrice} variant={Variant.CompactReverse} /> {/*TODO: HideOriginalPrice */}
          </PriceWithLabel>
        )}
        {compact ? (
          <TotalPrice price={item.price} variant={Variant.CompactReverse} /> //TODO: HideOriginalPrice
        ) : (
          <PriceWithLabel>
            <Label>{translate(t => t.miniCart.totalPriceLabel)}</Label>
            <TotalPrice price={item.price} variant={Variant.CompactReverse} /> {/*TODO: HideOriginalPrice */}
          </PriceWithLabel>
        )}
      </Bottom>
      {validationMessage && <ValidationMessage>{validationMessage}</ValidationMessage>}
    </ListItem>
  );
}

function changeQuantity(
  quantity: number,
  id: string,
  sku: string,
  setValidationMessage: (message: string | undefined) => void,
) {
  return new Promise((resolve, reject) => {
    return changeLineItemQuantity(id, quantity, {
      eventData: {
        sku,
        additionalProductAttributes: {
          ecommerce: { button_location: 'cart', button_name: 'change_quantity' },
        },
      }
    }).then(respone => {
      const validation = respone as Excite.Commercetools.CartValidationApiModel;
      if (validation?.hasErrors) {
        if (validation.validationErrors.find(e => e.code === 'insufficient-inventory')) {
          setValidationMessage(translate(t => t.productStockStatus.outOfStockMessage));
        }
        reject();
      } else {
        setValidationMessage(undefined);
        resolve(respone);
      }
    });
  });
}

const ValidationMessage = styled.div({
  color: t => t.negative.color,
  fontSize: t => t.fontSize.xxs,
  fontWeight: 500,
  marginTop: t => t.sharedSpacing.l,
  textAlign: 'right',
  gridColumn: { start: 1, end: 6 },
});

function cartItemsSelector(data?: Excite.Commercetools.CartApiModel) {
  if (data) {
    return data.items as Excite.Commercetools.PackageLineItemApiModel[];
  }
}

const LogoImagePlaceholder = styled(ImagePlaceholder, {
  height: '100%',
});

const Logo = styled(SiteLogo, absoluteCenterDecorator, {
  padding: { xy: t => t.sharedSpacing.xs },
});

const ItemImage = isNordicNest ? SquareImage : ImageWithBackgroundColor;
const Image = styled(ItemImage, {
  display: 'flex',
  height: '100%',
});

const PackageIncludeText = styled.h4({
  fontSize: t => t.fontSize.xxxs,
});

const PackageItems = styled.div({
  gridColumn: { start: 2, end: 6 },
  marginBottom: t => t.sharedSpacing.m,
});

const List = styled.ul(bareListDecorator);

const itemDecorator = styled(overflowEllipsisDecorator, {
  display: 'block',
  marginLeft: t => t.sharedSpacing.xs,
  fontSize: t => t.fontSize.xxxs,
  whiteSpace: 'nowrap',
  textDecoration: 'none',
  ':hover': {
    textDecoration: 'underline',
  },
});

const ProductConfigItem = styled.span(itemDecorator, {
  ':hover': {
    textDecoration: 'none',
  },
});

const PackageItem = styled(Link, itemDecorator);
const NoLinkPackageItem = styled.div(itemDecorator, {
  ':hover': {
    textDecoration: 'none',
  },
});

const QuoteItem = styled(PackageItem, {
  marginLeft: 0,
});

const DeliveryHeightAnimation = styled(SlideHeightAnimation, {
  textAlign: 'center',
  maxWidth: '100%',
  width: '100%',
  overflowX: 'hidden',
});

const DeliveryTitle = styled.h3({
  fontWeight: t => (isSvenssons ? t.fontWeight?.normal : t.fontWeight?.bold),
  fontSize: t => t.fontSize.s,
  textAlign: 'center',
  padding: { y: t => t.sharedSpacing.m },
  marginBottom: 0,
  borderBottom: { width: 1, style: 'solid', color: t => t.borderColor },
});

const Items = styled.ul(bareListDecorator);

const ListItem = styled.li(
  createMinLargeViewportDecorator({
    gridColumnGap: t => t.sharedSpacing.l,
  }),
  {
    display: 'grid',
    grid: {
      template: {
        columns: '130px 1fr auto auto auto',
      },
      column: { gap: t => t.sharedSpacing.m },
      auto: { flow: 'dense', rows: 'auto auto auto auto auto' },
    },
    alignItems: 'end',
    padding: { right: t => t.sharedSpacing.m },
    overflow: 'hidden',
    borderBottom: { width: 1, style: 'solid', color: t => t.borderColor },
  },
);

const imageHeightDecorator = styled({
  maxHeight: 130,
  alignSelf: 'self-start',
});

const imageWrapperDecorator = styled({
  gridRowStart: 'span 4',
  height: '100%',
});

const ImageWrapper = styled.div(imageWrapperDecorator);

const ImageLink = styled(
  Link,
  imageWrapperDecorator,
  !isSvenssons && {
    padding: { y: t => t.sharedSpacing.m, left: t => t.sharedSpacing.m },
  },
);

const StockStatus = isSvenssons ? SvenssonsStockStatus : GeneralStockStatus;
const ProductStockStatus = styled(StockStatus, {
  gridColumn: { start: 2, end: 6 },
  marginBottom: t => t.sharedSpacing.xs,
});

const Title = styled.div({
  fontSize: t => t.sharedFontSize.xs,
  gridColumn: { start: 2, end: 6 },
  marginBottom: t => t.sharedSpacing.xs,
});

const Remove = styled(Button, {
  fontSize: t => t.fontSize.xxs,
  padding: { xy: 0 },
});

const Brand = styled.div({
  textTransform: 'uppercase',
  fontSize: t => t.sharedFontSize.xxs,
  color: t => t.productCard?.brand?.color,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gridColumn: { start: 2, end: 6 },
  marginTop: t => t.sharedSpacing.m,
  marginBottom: 4,
  fontFamily: t => t.fontFamily?.primary,
});

const Bottom = styled.div({
  display: 'flex',
  gridColumn: { start: 2, end: 6 },
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  marginBottom: t => t.sharedSpacing.m,
});

const bottomGridDecorator = styled({
  display: 'grid',
  gridTemplateColumns: '1fr 100px 100px',
  gap: t => t.sharedSpacing.xxl,
});

const Quantity = styled(
  NumberField,
  {
    width: 85,
  },
  isSvenssons
    ? {
        outline: { width: 1, style: 'solid', color: t => t.white.border, offset: -1 },
        fontSize: t => t.sharedFontSize.s,
      }
    : {
        backgroundColor: t => t.monochromeBrighter.color,
        fontSize: t => t.sharedFontSize.xs,
      },
);

const Label = styled.div({ fontSize: t => t.fontSize.xxs });

const PriceWithLabel = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
});

const Price = isSvenssons ? SvenssonsPrice : GeneralPrice;
const UnitPrice = styled(Price, {
  justifySelf: 'end',
  fontSize: t => t.sharedFontSize.xs,
  fontWeight: t => t.fontWeight?.normal,
});

const TotalPrice = styled(Price, {
  textAlign: 'right',
  fontSize: t => t.sharedFontSize.xs,
  fontWeight: 'bold',
});

const imageDecortator = styled({
  height: '100%',
  padding: { xy: t => t.sharedSpacing.xs },
});
